import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LoadingButton from "../components/LoadingButton";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiLock,
  FiArrowLeft,
  FiCheck,
} from "react-icons/fi";
import LanguageSelector from "../components/LanguageSelector";

// Move InputField outside the SignUp component to avoid remounts on each render.
const InputField = React.memo(({ icon: Icon, ...props }) => (
  <div className="relative group">
    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
      <Icon className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
    </div>
    <input
      className="w-full pl-11 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-gray-900 placeholder-gray-400 font-medium"
      {...props}
    />
  </div>
));

export default function SignUp() {
  const { signup, verifyCode } = useAuth();
  const navigate = useNavigate();
  // Si viene de la herramienta gratuita de la web, trae la plantilla que usó y
  // el resultado que vio: el registro los importa para que su cuenta no llegue
  // vacía (?tpl=deliverynote&from=<resultId>).
  // Código de «Invita y gana»: en la URL (?ref=) o, si el visitante llegó antes por el enlace de un amigo, en la
  // cookie ws_ref que deja la landing (30 días).
  const referralCode = (() => {
    try {
      const q = new URLSearchParams(window.location.search).get("ref");
      if (/^[a-z2-7]{8}$/i.test(q || "")) return q.toLowerCase();
      const m = document.cookie.match(/(?:^|; )ws_ref=([a-z2-7]{8})/i);
      return m ? m[1].toLowerCase() : null;
    } catch {
      return null;
    }
  })();

  const trial = (() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const tpl = q.get("tpl");
      const from = q.get("from");
      const urlTrial = {
        tpl: /^[a-z_]{2,24}$/.test(tpl || "") ? tpl : null,
        from: /^[0-9a-f-]{36}$/i.test(from || "") ? from : null,
      };
      if (urlTrial.tpl && urlTrial.from) return { ...urlTrial, via: "url" };
      // Sin parámetros en la URL (clic central, otra pestaña, enlace copiado, URL
      // escrita a mano), la landing deja una cookie de primera parte en
      // .whappscan.com con la última prueba: se usa si no ha caducado (48 h).
      const m = document.cookie.match(/(?:^|; )ws_trial=([a-z_]{2,24})\.([0-9a-f-]{36})/i);
      if (m) return { tpl: m[1], from: m[2], via: "cookie" };
      return { ...urlTrial, via: null };
    } catch (e) {
      return { tpl: null, from: null, via: null };
    }
  })();
  const [landing, setLanding] = useState(null);
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [language, setLanguage] = useState(i18n.language || "es");

  const [verificationCode, setVerificationCode] = useState("");
  const [pendingUserId, setPendingUserId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  // Registro mínimo: nombre, email y contraseña. El número de WhatsApp y el
  // nombre de la empresa se piden en el onboarding, que ya tenía ese paso.
  const validateForm = () => {
    if (!name.trim()) return setError(t("signup.error_name_required")) || false;
    if (!email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
      return setError(t("signup.error_email_invalid")) || false;
    if (!password || password.length < 8)
      return setError(t("signup.error_password_length")) || false;
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await signup({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        language,
        ...(trial.tpl ? { from_template: trial.tpl } : {}),
        ...(trial.from ? { from_result: trial.from } : {}),
        ...(trial.via ? { trial_via: trial.via } : {}),
        ...(referralCode ? { ref: referralCode } : {}),
      });

      if (result.success) {
        if (window.fbq) {
          window.fbq('track', 'CompleteRegistration', {
            content_name: 'whappscan_signup',
            status: true,
            currency: 'EUR',
            value: 0,
          });
        }
        // Evento de GA4 que faltaba (solo había signup_click, que es intención): cuenta
        // creada, con la plantilla que probó y si la prueba llegó importada.
        if (window.gtag) {
          window.gtag("event", "sign_up", {
            method: "email",
            template: trial.tpl || "none",
            imported: Boolean(result.imported),
            trial_via: trial.via || "none",
          });
        }
        try {
          document.cookie = "ws_trial=; Max-Age=0; Path=/; Domain=.whappscan.com";
          document.cookie = "ws_ref=; Max-Age=0; Path=/; Domain=.whappscan.com";
        } catch (e) {
          /* sin cookies: no pasa nada */
        }
        setPendingUserId(result.user_id);
        // A dónde ir tras verificar: al documento importado si lo hay; si no,
        // a la plantilla que probó; si no, a subir documentos.
        const starters = result.starter_scanners || {};
        if (result.imported?.scanner_id) setLanding(`/scanners/${result.imported.scanner_id}`);
        else if (trial.tpl && starters[trial.tpl]) setLanding(`/scanners/${starters[trial.tpl]}`);
        else if (Object.keys(starters).length) setLanding("/upload");
        setStep(2);
      } else {
        setError(result.message || t("signup.error_generic"));
      }
    } catch (err) {
      const errorData = err.response?.data;
      setError(errorData?.message || t("signup.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    if (!verificationCode || verificationCode.length !== 6)
      return setError(t("signup.error_code_invalid"));

    setLoading(true);
    try {
      const result = await verifyCode({
        user_id: pendingUserId,
        code: verificationCode,
      });
      if (result.success) navigate(landing || "/onboarding", { state: { fromSignup: true } });
      else setError(result.message || t("signup.error_code_wrong"));
    } catch (err) {
      setError(err.response?.data?.message || t("signup.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await signup({ user_id: pendingUserId, resend: true });
      if (result.success) alert(t("signup.code_resent"));
      else setError(result.message || t("signup.error_resend"));
    } catch (err) {
      setError(t("signup.error_resend"));
    } finally {
      setLoading(false);
    }
  };

  // --- UI Components ---

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gray-50 py-12 px-4">
      {/* Dynamic Background */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-primary-200/20 rounded-full blur-[100px] translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-[60%] h-[60%] bg-blue-200/20 rounded-full blur-[100px] -translate-x-1/2 translate-y-1/2" />
      </div>

      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl relative z-10"
      >
        <div className="bg-white/80 backdrop-blur-xl border border-white/60 p-8 md:p-12 rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="text-center mb-10">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
              className="bg-gradient-to-br from-primary-50 to-emerald-50 rounded-2xl p-4 w-auto h-20 mx-auto mb-6 shadow-sm border border-white flex items-center justify-center"
            >
              <img src="/WhappScan.png" alt="Logo" className="h-10 w-auto" />
            </motion.div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">
              {step === 1 ? t("signup.title") : t("signup.verify_title")}
            </h1>
            <p className="text-gray-500 font-medium">
              {step === 1 ? t("app.welcome") : t("signup.verify_message")}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8"
              >
                <div className="p-4 bg-red-50/80 border border-red-100 text-red-600 rounded-xl text-sm font-medium text-center">
                  {error}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.form
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                onSubmit={handleRegister}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                {trial.from && (
                  <p
                    data-testid="signup-trial-note"
                    className="lg:col-span-2 rounded-xl bg-primary-50 border border-primary-100 px-4 py-3 text-sm font-medium text-primary-800"
                  >
                    {t("signup.trial_waiting")}
                  </p>
                )}
                <InputField
                  icon={FiUser}
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("signup.name_placeholder")}
                  label={t("signup.name")}
                />
                <InputField
                  icon={FiMail}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  label={t("signup.email")}
                />

                <div>
                  <InputField
                    icon={FiLock}
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("signup.password_placeholder")}
                    label={t("signup.password")}
                  />
                  {/* En el móvil, escribir 8 caracteres a ciegas es donde se abandona el formulario. */}
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="mt-2 text-sm font-semibold text-primary-700 hover:underline"
                  >
                    {showPassword ? t("signup.hide_password") : t("signup.show_password")}
                  </button>
                </div>
                {/* Language Selector */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    {t("signup.language")}
                  </label>
                  <LanguageSelector value={language} onChange={setLanguage} />
                </div>

                <div className="lg:col-span-2 pt-4">
                  <LoadingButton
                    type="submit"
                    loading={loading}
                    className="w-full justify-center bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white py-4 rounded-xl font-bold shadow-lg shadow-primary-600/20 hover:shadow-xl hover:shadow-primary-600/30 transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    {t("signup.register_button")}
                  </LoadingButton>
                </div>

                <p className="lg:col-span-2 text-center text-sm text-gray-500 font-medium mt-2">
                  {t("signup.have_account")}{" "}
                  <Link
                    to="/login"
                    className="text-primary-600 hover:text-primary-700 font-bold hover:underline"
                  >
                    {t("signup.login_link")}
                  </Link>
                </p>
              </motion.form>
            ) : (
              <motion.form
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                onSubmit={handleVerify}
                className="space-y-8 max-w-md mx-auto"
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-50 text-primary-600 mb-4">
                    <FiCheck className="w-8 h-8" />
                  </div>
                  <p className="text-lg font-bold text-gray-900">
                    {email}
                  </p>
                </div>

                <div>
                  <input
                    type="text"
                    className="w-full border-2 border-primary-100 bg-white p-4 rounded-2xl text-center text-4xl tracking-[0.5em] font-mono focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 transition-all text-primary-900 placeholder-primary-200"
                    value={verificationCode}
                    onChange={(e) =>
                      setVerificationCode(
                        e.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>

                <LoadingButton
                  type="submit"
                  loading={loading}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-4 rounded-xl font-bold shadow-lg transition-all transform hover:-translate-y-0.5"
                  disabled={verificationCode.length !== 6}
                >
                  {t("signup.verify_button")}
                </LoadingButton>

                <div className="flex justify-between items-center text-sm">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-gray-500 hover:text-gray-700 font-medium flex items-center gap-1"
                  >
                    <FiArrowLeft className="w-4 h-4" />{" "}
                    {t("signup.back_to_form")}
                  </button>
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={loading}
                    className="text-primary-600 hover:text-primary-700 font-bold hover:underline"
                  >
                    {t("signup.resend_code")}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
